import { computed, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
} from '@ngrx/signals';
import {
  entityConfig,
  removeEntity,
  setEntities,
  updateEntity,
  withEntities,
} from '@ngrx/signals/entities';
import { PreferenceService } from '@thiememeulenhoff/ng-api-gateway';
import { MethodPreference } from '@thiememeulenhoff/types';
import {
  RequestStatus,
  withRequestStatus,
} from '../_utils/request-status.feature';

const methodPreferenceEntity = entityConfig({
  entity: {} as MethodPreference,
  selectId: (e: MethodPreference) => e.method,
});

export const PreferenceStore = signalStore(
  { providedIn: 'root' },
  withEntities(methodPreferenceEntity),
  withRequestStatus(),
  withComputed(({ entities }) => ({
    methodPreferences: computed(() => entities()),
  })),
  withMethods((store, preferenceService = inject(PreferenceService)) => ({
    setMethodPreferences(preferences: Array<MethodPreference>): void {
      patchState(store, setEntities(preferences, methodPreferenceEntity));
    },
    async fetchMethodPreferences(): Promise<void> {
      try {
        const preferences = await firstValueFrom(
          preferenceService.fetchPreferences(),
        );
        patchState(store, setEntities(preferences, methodPreferenceEntity), {
          requestStatus: 'success' as RequestStatus,
        });
      } catch (e: unknown) {
        const error = e instanceof Error ? e : new Error('Failed to fetch');
        patchState(store, {
          requestStatus: {
            error: error.message || 'Failed to fetch',
          } as RequestStatus,
        });
      }
    },

    async setMethodPreference(preference: MethodPreference): Promise<void> {
      await firstValueFrom(preferenceService.setMethodPreference(preference));
      patchState(
        store,
        updateEntity(
          { id: preference.method, changes: preference },
          methodPreferenceEntity,
        ),
      );
      const preferences = await firstValueFrom(
        preferenceService.fetchPreferences(),
      );
      patchState(store, setEntities(preferences, methodPreferenceEntity), {
        requestStatus: 'success' as RequestStatus,
      });
    },

    async deleteMethodPreference(methodSlug: string): Promise<void> {
      await firstValueFrom(
        preferenceService.deleteMethodPreference(methodSlug),
      );
      patchState(store, removeEntity(methodSlug));
      const preferences = await firstValueFrom(
        preferenceService.fetchPreferences(),
      );
      patchState(store, setEntities(preferences, methodPreferenceEntity), {
        requestStatus: 'success' as RequestStatus,
      });
    },

    async addArrangementToMethodPreference(
      methodSlug: string,
      arrangementCode: string,
    ): Promise<void> {
      await firstValueFrom(
        preferenceService.addArrangementToMethodPreference(
          methodSlug,
          arrangementCode,
        ),
      );
      const preferences = await firstValueFrom(
        preferenceService.fetchPreferences(),
      );
      patchState(store, setEntities(preferences, methodPreferenceEntity), {
        requestStatus: 'success' as RequestStatus,
      });
    },

    async deleteArrangementFromMethodPreference(
      methodSlug: string,
      arrangementCode: string,
      delayStateChangeInMs = 0,
    ): Promise<void> {
      const currentTimestamp = Date.now();
      await firstValueFrom(
        preferenceService.deleteArrangementFromMethodPreference(
          methodSlug,
          arrangementCode,
        ),
      );

      const delayUntil = delayStateChangeInMs - (Date.now() - currentTimestamp);

      if (delayUntil > 0) {
        await new Promise<void>((resolve) => {
          setTimeout(() => resolve(), delayUntil);
        });
      }

      const preferences = await firstValueFrom(
        preferenceService.fetchPreferences(),
      );
      patchState(store, setEntities(preferences, methodPreferenceEntity), {
        requestStatus: 'success' as RequestStatus,
      });
    },
  })),
  withHooks({
    onInit(store) {
      store.fetchMethodPreferences();
    },
  }),
);
